import type { ReactNode } from 'react';
import { Button } from '@carbon/react';
import classNames from 'classnames';
import { FilterReset } from '@carbon/react/icons';
import { HorizontalDraggableDivider } from 'components/draggable-divider';
import styles from './collapsible-filter-panel.module.scss';

interface FixedWidth {
  width?: number;
  onResize?: never;
  minWidth?: never;
  maxWidth?: never;
}

interface Resizable {
  /** The rail's width in px, in place of its fixed default. */
  width: number;
  /** Makes the rail's edge a drag handle, reporting the new width within bounds. */
  onResize: (width: number) => void;
  minWidth: number;
  maxWidth: number;
}

type CollapsibleFilterPanelProps = {
  children: ReactNode;
  filterCount: number;
  onReset: () => void;
  isOpen: boolean;
  /**
   * Scroll the rail's own overflow instead of growing the page. Off by default: it clips
   * anything drawn outside the rail (flatpickr calendars mount on body, so they're fine).
   */
  scrollable?: boolean;
  /** Keep the Reset row in view while a scrolling rail scrolls under it. */
  stickyHeader?: boolean;
  /** Controls at the far end of the Reset row. */
  headerActions?: ReactNode;
} & (FixedWidth | Resizable);

/**
 * A page's filter rail: Reset Filters above the caller's filters, shown while
 * open.
 * @param {CollapsibleFilterPanelProps} props The rail.
 * @return {JSX.Element | null} The rail.
 */
export function CollapsibleFilterPanel({
  children,
  filterCount,
  onReset,
  isOpen,
  scrollable = false,
  stickyHeader = false,
  headerActions,
  width,
  onResize,
  minWidth,
  maxWidth,
}: CollapsibleFilterPanelProps) {
  if (!isOpen) {
    return null;
  }

  const panel = (
    <div
      className={classNames(styles.sidebar, {
        [styles.sidebarScrolls]: scrollable,
      })}
      style={width === undefined ? undefined : { width }}
    >
      <div
        className={classNames(styles.resetFilters, {
          [styles.headerWithActions]: headerActions,
          [styles.stickyHeader]: stickyHeader,
        })}
      >
        <Button
          kind="ghost"
          size="lg"
          onClick={onReset}
          disabled={filterCount === 0}
          className={styles.resetFiltersButton}
          renderIcon={FilterReset}
        >
          Reset Filters
        </Button>
        {headerActions}
      </div>
      <div className={styles.content}>{children}</div>
    </div>
  );

  if (!onResize) return panel;

  return (
    <div className={styles.resizable}>
      {panel}
      <HorizontalDraggableDivider
        side="left"
        onDrag={onResize}
        minWidth={minWidth}
        maxWidth={maxWidth}
        value={width}
        label="Resize filter panel"
      />
    </div>
  );
}
